import { availableLanguages, ISO639Lang } from "../../constans/languagesList";
import {
  LocalizedText,
  ChapterQuiz,
  NewChapterQuiz,
  QuizChapter,
  Quiz,
  QuizEvaluationType,
  QuizQuestionType,
  QuizType,
} from "../../types/Quiz";
import {
  getQuizQuestions,
  hasQuizChapters,
  normalizeQuizChaptersForAuthoring,
} from "../../utils/quizChapters";
import {
  getInterpretationFormulaOutputRange,
  normalizeFormulaQuestionReferences,
  parseInterpretationFormula,
} from "./interpretationFormula";

export type QuizFormAnswer = {
  id: string;
  originalId?: string;
  text: LocalizedText[];
  isCorrect: boolean;
  staticPoints: string;
  riskFlag?: boolean;
};

export type QuizFormQuestion = {
  id: string;
  originalId?: string;
  chapterId?: string;
  text: LocalizedText[];
  type: QuizQuestionType;
  points: string;
  range: { from: string; to: string };
  correctRange: { from: string; to: string };
  answers: QuizFormAnswer[];
};

export type QuizFormChapter = {
  id: string;
  originalId?: string;
  title?: LocalizedText[];
  description?: LocalizedText[];
};

export type QuizFormInterpretationBand = {
  id: string;
  range: { from: string; to: string };
  text: LocalizedText[];
};

export type QuizFormInterpretation = {
  id: string;
  title: LocalizedText[];
  formula: string;
  bands: QuizFormInterpretationBand[];
};

export type QuizFormState = {
  title: LocalizedText[];
  description: LocalizedText[];
  selectedLanguages: string[];
  type: QuizType | "";
  evaluationType: QuizEvaluationType | "";
  minimumPoints: string;
  retakeAmount: string;
  isConfidential: boolean;
  isEditable: boolean;
  isScoreShown: boolean;
  questions: QuizFormQuestion[];
  chapters: QuizFormChapter[];
  interpretations: QuizFormInterpretation[];
};

const makeLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyChapter = (): QuizFormChapter => ({
  id: makeLocalId("chapter"),
});

export const getQuestionChapterId = (state: QuizFormState, question: QuizFormQuestion) =>
  question.chapterId ?? state.chapters[0]?.id;

export const getChapterQuestions = (state: QuizFormState, chapterId: string) =>
  state.questions.filter((question) => getQuestionChapterId(state, question) === chapterId);

const bindQuestionChapters = (state: QuizFormState): QuizFormState => ({
  ...state,
  questions: state.questions.map((question) => ({
    ...question,
    chapterId: getQuestionChapterId(state, question),
  })),
});

export const moveFormChapter = (
  state: QuizFormState,
  chapterId: string,
  direction: -1 | 1,
): QuizFormState => {
  const index = state.chapters.findIndex((chapter) => chapter.id === chapterId);
  const destination = index + direction;
  if (index < 0 || destination < 0 || destination >= state.chapters.length) return state;
  const chapters = [...state.chapters];
  [chapters[index], chapters[destination]] = [chapters[destination], chapters[index]];
  return { ...bindQuestionChapters(state), chapters };
};

export const canDeleteFormChapter = (state: QuizFormState, chapterId: string): boolean =>
  state.chapters.length > 1 &&
  state.chapters.some((chapter) => chapter.id === chapterId) &&
  getChapterQuestions(state, chapterId).length === 0;

export const deleteFormChapter = (state: QuizFormState, chapterId: string): QuizFormState =>
  canDeleteFormChapter(state, chapterId)
    ? { ...bindQuestionChapters(state), chapters: state.chapters.filter((chapter) => chapter.id !== chapterId) }
    : state;

export const moveFormQuestion = (
  state: QuizFormState,
  questionId: string,
  direction: -1 | 1,
): QuizFormState => {
  const questions = getFormQuestions(state);
  const index = questions.findIndex((question) => question.id === questionId);
  const destination = index + direction;
  if (index < 0 || destination < 0 || destination >= questions.length ||
    getQuestionChapterId(state, questions[index]) !== getQuestionChapterId(state, questions[destination])) return state;
  [questions[index], questions[destination]] = [questions[destination], questions[index]];
  return { ...state, questions };
};

/** Moving to another chapter appends there; persisted identities remain unchanged. */
export const moveQuestionToChapter = (
  state: QuizFormState,
  questionId: string,
  chapterId: string,
): QuizFormState => {
  const question = state.questions.find((item) => item.id === questionId);
  if (!question || !state.chapters.some((chapter) => chapter.id === chapterId) ||
    getQuestionChapterId(state, question) === chapterId) return state;
  return { ...state, questions: [
    ...state.questions.filter((item) => item.id !== questionId),
    { ...question, chapterId },
  ] };
};

/** The flat list is draft state only; chapter order owns global question positions. */
export const getFormQuestions = (state: QuizFormState): QuizFormQuestion[] =>
  getQuizQuestions({
    chapters: state.chapters.map((chapter) => ({
      questions: getChapterQuestions(state, chapter.id),
    })),
  });

export const quizTypeLabels: Record<QuizType, string> = {
  [QuizType.COMPANIES_DIAGNOSTIC]: "Companies Diagnostic",
  [QuizType.FEEDBACK]: "Feedback",
};

export const quizTypeOptions = Object.values(QuizType).map((type) => ({
  value: type,
  lable: quizTypeLabels[type],
}));

export const evaluationTypeLabels: Record<QuizEvaluationType, string> = {
  [QuizEvaluationType.SCORE]: "Score",
  [QuizEvaluationType.INTERPRETATION]: "Interpretation",
  [QuizEvaluationType.RESPONSE_ONLY]: "Response-only",
};

export const evaluationTypeOptions = Object.values(QuizEvaluationType).map(
  (type) => ({ value: type, lable: evaluationTypeLabels[type] }),
);

export const languageOptions = availableLanguages.map((language) => ({
  value: language,
  lable: ISO639Lang[language],
}));

export const shouldUseQuizScoreSettings = (
  evaluationType: QuizEvaluationType | "",
) => evaluationType === QuizEvaluationType.SCORE;

export const isFeedbackQuizType = (type: QuizType | "") =>
  type === QuizType.FEEDBACK;

export const isFeedbackResponseOnlyTemplate = (state: QuizFormState) =>
  isFeedbackQuizType(state.type) &&
  state.evaluationType === QuizEvaluationType.RESPONSE_ONLY;

export const getEvaluationTypeOptionsForQuizType = (type: QuizType | "") =>
  evaluationTypeOptions.filter((option) =>
    isFeedbackQuizType(type)
      ? option.value !== QuizEvaluationType.SCORE
      : true,
  );

export const canUseTextQuestions = (
  quizType: QuizType | "",
  evaluationType: QuizEvaluationType | "",
) =>
  evaluationType !== "" &&
  (!isFeedbackQuizType(quizType) || evaluationType !== QuizEvaluationType.SCORE);

export const getQuestionTypeOptions = (
  quizType: QuizType | "",
  evaluationType: QuizEvaluationType | "",
) => [
  {
    value: QuizQuestionType.SINGLE,
    lable: "Single",
  },
  {
    value: QuizQuestionType.MULTIPLE,
    lable: "Multiple",
  },
  {
    value: QuizQuestionType.RANGE,
    lable: "Range",
  },
  ...(canUseTextQuestions(quizType, evaluationType)
    ? [
        {
          value: QuizQuestionType.TEXT,
          lable: "Text",
        },
      ]
    : []),
];

export const makeLocalizedText = (languages: string[], text = "") =>
  languages.map((language) => ({ language, text }));

export const syncLocalizedTextLanguages = (
  value: LocalizedText[] | undefined,
  languages: string[],
) =>
  languages.map((language) => ({
    language,
    text: value?.find((item) => item.language === language)?.text ?? "",
  }));

export const getTextForLanguage = (
  value: LocalizedText[] | undefined,
  language: string,
) => value?.find((item) => item.language === language)?.text ?? "";

export const setTextForLanguage = (
  value: LocalizedText[],
  language: string,
  text: string,
) =>
  value.map((item) => (item.language === language ? { ...item, text } : item));

export const createEmptyAnswer = (languages: string[]): QuizFormAnswer => ({
  riskFlag: false,
  id: makeLocalId("answer"),
  text: makeLocalizedText(languages),
  isCorrect: false,
  staticPoints: "",
});

export const createEmptyQuestion = (
  languages: string[],
  evaluationType: QuizEvaluationType | "" = "",
): QuizFormQuestion => ({
  id: makeLocalId("question"),
  text: makeLocalizedText(languages),
  type: QuizQuestionType.SINGLE,
  points: evaluationType === QuizEvaluationType.SCORE ? "1" : "",
  range: { from: "", to: "" },
  correctRange: { from: "", to: "" },
  answers: [createEmptyAnswer(languages), createEmptyAnswer(languages)],
});

export const createEmptyInterpretation = (
  languages: string[],
): QuizFormInterpretation => ({
  id: makeLocalId("interpretation"),
  title: makeLocalizedText(languages),
  formula: "",
  bands: [createEmptyInterpretationBand(languages)],
});

export const createEmptyInterpretationBand = (
  languages: string[],
  suggestedFrom = "0",
  suggestedTo = "0",
): QuizFormInterpretationBand => ({
  id: makeLocalId("interpretation-band"),
  range: { from: suggestedFrom, to: suggestedTo },
  text: makeLocalizedText(languages),
});

export const duplicateInterpretation = (
  interpretation: QuizFormInterpretation,
): QuizFormInterpretation => ({
  ...interpretation,
  id: makeLocalId("interpretation"),
  title: interpretation.title.map((item) => ({ ...item })),
  bands: interpretation.bands.map((band) => ({
    ...band,
    id: makeLocalId("interpretation-band"),
    range: { ...band.range },
    text: band.text.map((item) => ({ ...item })),
  })),
});

const emptyRange = () => ({ from: "", to: "" });

const hasAtMostTwoDecimalPlaces = (value: string) =>
  /^-?\d+(?:\.\d{1,2})?$/.test(value.trim());

const isFiniteTwoDecimalNumber = (text: string, value: number) =>
  Number.isFinite(value) && hasAtMostTwoDecimalPlaces(text);

const getLastCoveredRoundedResult = (to: number) => Math.floor(to);

const getFirstCoveredRoundedResult = (from: number) => Math.ceil(from);

const getExpectedNextBandFrom = (previousTo: number) =>
  getLastCoveredRoundedResult(previousTo) + 1;

export const getInterpretationBandPreview = (
  band: QuizFormInterpretationBand,
) => {
  const from = band.range.from.trim();
  const to = band.range.to.trim();
  if (!from && !to) return "Set at least one bound";
  if (!from) return `≤ ${to}`;
  if (!to) return `≥ ${from}`;
  if (from === to) return from;
  return `${from}–${to}`;
};

export const getInterpretationBandErrors = (
  bands: QuizFormInterpretationBand[],
): string[][] => {
  const errors = bands.map(() => [] as string[]);

  bands.forEach((band, index) => {
    const fromText = band.range.from.trim();
    const toText = band.range.to.trim();
    const from = Number(fromText);
    const to = Number(toText);

    if (!fromText && !toText) {
      errors[index].push("At least one bound is required.");
    }
    if (fromText && !isFiniteTwoDecimalNumber(fromText, from)) {
      errors[index].push(
        "From must be a finite number with at most 2 decimal places.",
      );
    }
    if (toText && !isFiniteTwoDecimalNumber(toText, to)) {
      errors[index].push(
        "To must be a finite number with at most 2 decimal places.",
      );
    }
    if (!fromText && index !== 0) {
      errors[index].push("Only the first band may have no lower bound.");
    }
    if (!toText && index !== bands.length - 1) {
      errors[index].push("Only the last band may have no upper bound.");
    }
    if (
      fromText &&
      toText &&
      isFiniteTwoDecimalNumber(fromText, from) &&
      isFiniteTwoDecimalNumber(toText, to) &&
      from > to
    ) {
      errors[index].push("From must not exceed To.");
    }
  });

  for (let index = 1; index < bands.length; index += 1) {
    const previousToText = bands[index - 1].range.to.trim();
    const currentFromText = bands[index].range.from.trim();
    const previousTo = Number(previousToText);
    const currentFrom = Number(currentFromText);

    if (
      previousToText &&
      currentFromText &&
      isFiniteTwoDecimalNumber(previousToText, previousTo) &&
      isFiniteTwoDecimalNumber(currentFromText, currentFrom)
    ) {
      const expectedFrom = getExpectedNextBandFrom(previousTo);
      const previousCoveredThrough = getLastCoveredRoundedResult(previousTo);
      const currentCoveredFrom = getFirstCoveredRoundedResult(currentFrom);
      if (currentCoveredFrom > expectedFrom) {
        errors[index - 1].push("There is a gap after this band.");
        errors[index].push(`Gap: this band should start at ${expectedFrom}.`);
      } else if (currentCoveredFrom <= previousCoveredThrough) {
        errors[index - 1].push("This band overlaps the next band.");
        errors[index].push(
          `Overlap: this band should start at ${expectedFrom}.`,
        );
      }
    }
  }

  return errors;
};

type InterpretationOutputRange = {
  min: number;
  max: number;
};

const getIntegerRangeText = (from: number, to: number) =>
  from === to ? String(from) : `${from} through ${to}`;

const getQuestionFormulaRange = (
  question: QuizFormQuestion,
  position: number,
) => {
  if (question.type === QuizQuestionType.TEXT) return null;

  if (question.type === QuizQuestionType.RANGE) {
    const from = Number(question.range.from);
    const to = Number(question.range.to);
    if (
      question.range.from.trim() === "" ||
      question.range.to.trim() === "" ||
      !Number.isFinite(from) ||
      !Number.isFinite(to) ||
      from > to
    ) {
      return null;
    }

    return { position, min: from, max: to };
  }

  const values = question.answers
    .map((answer) => Number(answer.staticPoints))
    .filter((value) => Number.isFinite(value));

  if (!values.length || values.length !== question.answers.length) return null;

  if (question.type === QuizQuestionType.MULTIPLE) {
    return {
      position,
      min: values.reduce((total, value) => total + Math.min(0, value), 0),
      max: values.reduce((total, value) => total + Math.max(0, value), 0),
    };
  }

  return {
    position,
    min: Math.min(...values),
    max: Math.max(...values),
  };
};

export const getInterpretationOutputRange = (
  formula: string,
  questions: QuizFormQuestion[],
) => {
  const referenceablePositions = questions
    .map((question, index) =>
      question.type === QuizQuestionType.TEXT ? null : index + 1,
    )
    .filter((position): position is number => position !== null);
  const referenceRanges = questions
    .map((question, index) => getQuestionFormulaRange(question, index + 1))
    .filter(
      (range): range is { position: number; min: number; max: number } =>
        range !== null,
    );

  return getInterpretationFormulaOutputRange(
    formula,
    questions.length,
    referenceRanges,
    { referenceablePositions },
  );
};

export const getInterpretationCoverageErrors = (
  bands: QuizFormInterpretationBand[],
  outputRange: InterpretationOutputRange,
) => {
  const errors: string[] = [];
  let coveredThrough = outputRange.min - 1;

  bands.forEach((band, index) => {
    const fromText = band.range.from.trim();
    const toText = band.range.to.trim();
    const from = Number(fromText);
    const to = Number(toText);

    if (
      (fromText && !isFiniteTwoDecimalNumber(fromText, from)) ||
      (toText && !isFiniteTwoDecimalNumber(toText, to))
    ) {
      return;
    }

    const start = Math.max(
      outputRange.min,
      fromText ? Math.ceil(from) : outputRange.min,
    );
    const end = Math.min(
      outputRange.max,
      toText ? Math.floor(to) : outputRange.max,
    );

    if (end < outputRange.min || start > outputRange.max || start > end) {
      return;
    }

    if (start <= coveredThrough) {
      errors.push(
        `Result ${start} is covered by more than one band; adjust the boundary before band ${index + 1}.`,
      );
    } else if (start > coveredThrough + 1) {
      errors.push(
        `No result band covers formula result ${getIntegerRangeText(
          coveredThrough + 1,
          start - 1,
        )}.`,
      );
    }

    coveredThrough = Math.max(coveredThrough, end);
  });

  if (coveredThrough < outputRange.max) {
    errors.push(
      `No result band covers formula result ${getIntegerRangeText(
        coveredThrough + 1,
        outputRange.max,
      )}.`,
    );
  }

  return errors;
};

export const moveInterpretationBand = (
  bands: QuizFormInterpretationBand[],
  index: number,
  direction: -1 | 1,
) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= bands.length) return bands;

  const next = [...bands];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
};

export const questionTypeChangeLosesData = (
  question: QuizFormQuestion,
  nextType: QuizQuestionType,
) => {
  if (question.type === nextType) return false;

  if (nextType === QuizQuestionType.TEXT) {
    return (
      question.answers.some(
        (answer) =>
          answer.riskFlag || answer.isCorrect ||
          answer.staticPoints.trim() !== "" ||
          answer.text.some((item) => item.text.trim() !== ""),
      ) ||
      [
        question.points,
        question.range.from,
        question.range.to,
        question.correctRange.from,
        question.correctRange.to,
      ].some((value) => value.trim() !== "")
    );
  }

  if (nextType === QuizQuestionType.RANGE) {
    return question.answers.some(
      (answer) =>
        answer.riskFlag || answer.isCorrect ||
        answer.staticPoints.trim() !== "" ||
        answer.text.some((item) => item.text.trim() !== ""),
    );
  }

  if (
    question.type === QuizQuestionType.RANGE ||
    question.type === QuizQuestionType.TEXT
  ) {
    return [
      question.points,
      question.range.from,
      question.range.to,
      question.correctRange.from,
      question.correctRange.to,
    ].some((value) => value.trim() !== "");
  }

  return false;
};

export const normalizeQuestionType = (
  question: QuizFormQuestion,
  nextType: QuizQuestionType,
  languages: string[],
  evaluationType: QuizEvaluationType = QuizEvaluationType.SCORE,
): QuizFormQuestion => {
  if (question.type === nextType) return question;

  if (nextType === QuizQuestionType.TEXT) {
    return {
      ...question,
      type: nextType,
      points: evaluationType === QuizEvaluationType.SCORE ? question.points || "1" : "",
      range: emptyRange(),
      correctRange: emptyRange(),
      answers: [],
    };
  }

  if (nextType === QuizQuestionType.RANGE) {
    return {
      ...question,
      type: nextType,
      points:
        evaluationType === QuizEvaluationType.INTERPRETATION ||
        evaluationType === QuizEvaluationType.RESPONSE_ONLY
          ? ""
          : question.points || "1",
      range: emptyRange(),
      correctRange: emptyRange(),
      answers: [],
    };
  }

  const answers =
    question.type === QuizQuestionType.RANGE ||
    question.type === QuizQuestionType.TEXT
      ? [createEmptyAnswer(languages), createEmptyAnswer(languages)]
      : question.answers;

  const normalizedAnswers = answers.map((answer, index) => ({
    ...answer,
    isCorrect:
      evaluationType === QuizEvaluationType.INTERPRETATION
        ? false
        : nextType === QuizQuestionType.SINGLE
          ? index === answers.findIndex((item) => item.isCorrect)
          : answer.isCorrect,
    staticPoints:
      evaluationType === QuizEvaluationType.SCORE &&
      nextType === QuizQuestionType.SINGLE
        ? ""
        : answer.staticPoints,
  }));

  return {
    ...question,
    type: nextType,
    points:
      evaluationType === QuizEvaluationType.INTERPRETATION ||
      evaluationType === QuizEvaluationType.RESPONSE_ONLY
        ? ""
        : nextType === QuizQuestionType.MULTIPLE
          ? ""
          : question.points || "1",
    range: emptyRange(),
    correctRange: emptyRange(),
    answers: normalizedAnswers,
  };
};

export const evaluationTypeChangeLosesData = (
  state: QuizFormState,
  nextType: QuizEvaluationType,
) =>
  Boolean(
    state.evaluationType &&
    state.evaluationType !== nextType &&
    (state.questions.length || state.interpretations.length),
  );

export const normalizeEvaluationType = (
  state: QuizFormState,
  nextType: QuizEvaluationType,
): QuizFormState => {
  if (state.evaluationType === nextType) return state;

  if (nextType === QuizEvaluationType.INTERPRETATION) {
    return {
      ...state,
      evaluationType: nextType,
      questions: state.questions.map((question) => ({
        ...question,
        points: "",
        correctRange: emptyRange(),
        answers: question.answers.map((answer) => ({
          ...answer,
          isCorrect: false,
        })),
      })),
      interpretations:
        state.evaluationType !== QuizEvaluationType.RESPONSE_ONLY &&
        state.interpretations.length
          ? state.interpretations
          : [createEmptyInterpretation(state.selectedLanguages)],
    };
  }

  if (nextType === QuizEvaluationType.RESPONSE_ONLY) {
    return {
      ...state,
      evaluationType: nextType,
      minimumPoints: "",
      ...(isFeedbackQuizType(state.type) ? {
        retakeAmount: "1",
        isConfidential: false,
        isEditable: false,
        isScoreShown: false,
      } : {}),
      questions: state.questions.map((question) => ({
        ...question,
        points: "",
        correctRange: emptyRange(),
        answers: question.answers.map((answer) => ({
          ...answer,
          isCorrect: false,
          staticPoints: "",
        })),
      })),
      interpretations: [],
    };
  }

  return {
    ...state,
    evaluationType: nextType,
    minimumPoints: state.minimumPoints || "1",
    questions: state.questions.map((question) => ({
      ...question,
      points:
        question.type === QuizQuestionType.MULTIPLE
          ? ""
          : question.points || "1",
      correctRange:
        question.type === QuizQuestionType.RANGE
          ? emptyRange()
          : question.correctRange,
      answers: question.answers.map((answer) => ({
        ...answer,
        isCorrect: false,
        staticPoints:
          question.type === QuizQuestionType.SINGLE ? "" : answer.staticPoints,
      })),
    })),
    interpretations: [],
  };
};

export const createInitialQuizFormState = (): QuizFormState => {
  const chapter = createEmptyChapter();
  return {
    title: [],
    description: [],
    selectedLanguages: [],
    type: "",
    evaluationType: "",
    minimumPoints: "1",
    retakeAmount: "1",
    isConfidential: false,
    isEditable: true,
    isScoreShown: true,
    chapters: [chapter],
    questions: [{ ...createEmptyQuestion([]), chapterId: chapter.id }],
    interpretations: [],
  };
};

export const quizToFormState = (quiz: Quiz): QuizFormState => {
  const title = quiz.title ?? [];
  const description = quiz.description ?? [];
  if (!hasQuizChapters(quiz.quiz)) {
    throw new Error("Unable to load this Quiz: expected quiz.chapters. Check the backend migration.");
  }
  const sourceChapters = normalizeQuizChaptersForAuthoring(quiz.quiz.chapters);
  const chapters = sourceChapters.map((chapter) => ({
    id: chapter._id ?? makeLocalId("chapter"),
    originalId: chapter._id,
    ...(chapter.title !== undefined ? { title: chapter.title.map((item) => ({ ...item })) } : {}),
    ...(chapter.description !== undefined ? { description: chapter.description.map((item) => ({ ...item })) } : {}),
  }));
  const questions = getQuizQuestions({ chapters: sourceChapters });
  const interpretations = quiz.interpretations ?? [];
  const languages = Array.from(
    new Set([
      ...title.map((item) => item.language),
      ...description.map((item) => item.language),
      ...sourceChapters.flatMap((chapter) => [
        ...(chapter.title ?? []).map((item) => item.language),
        ...(chapter.description ?? []).map((item) => item.language),
      ]),
      ...questions.flatMap((question) => [
        ...(question.text ?? []).map((item) => item.language),
        ...(question.answers ?? []).flatMap((answer) =>
          (answer.text ?? []).map((item) => item.language),
        ),
      ]),
      ...interpretations.flatMap((interpretation) => [
        ...interpretation.title.map((item) => item.language),
        ...interpretation.interpretation.flatMap((band) =>
          band.text.map((item) => item.language),
        ),
      ]),
    ]),
  );

  return {
    title: syncLocalizedTextLanguages(title, languages),
    description: syncLocalizedTextLanguages(description, languages),
    selectedLanguages: languages,
    type: quiz.type ?? "",
    evaluationType: quiz.evaluationType ?? "",
    minimumPoints:
      quiz.quiz?.minimumPoints === undefined
        ? "1"
        : String(quiz.quiz.minimumPoints),
    retakeAmount:
      quiz.quiz?.retakeAmount === undefined
        ? "1"
        : String(quiz.quiz.retakeAmount),
    isConfidential: quiz.isConfidential ?? false,
    isEditable: quiz.isEditable ?? true,
    isScoreShown: quiz.isScoreShown ?? true,
    chapters,
    questions: questions.map((question) => ({
      id: question._id ?? makeLocalId("question"),
      originalId: question._id,
      chapterId: chapters[sourceChapters.findIndex((chapter) => chapter.questions.includes(question))].id,
      text: syncLocalizedTextLanguages(question.text, languages),
      type: question.type,
      points: question.points === undefined ||
        (question.type === QuizQuestionType.TEXT && quiz.evaluationType !== QuizEvaluationType.SCORE)
        ? "" : String(question.points),
      range: question.type === QuizQuestionType.TEXT ? emptyRange() : {
        from:
          question.range?.from === undefined ? "" : String(question.range.from),
        to: question.range?.to === undefined ? "" : String(question.range.to),
      },
      correctRange: question.type === QuizQuestionType.TEXT ? emptyRange() : {
        from:
          question.correctRange?.from === undefined
            ? ""
            : String(question.correctRange.from),
        to:
          question.correctRange?.to === undefined
            ? ""
            : String(question.correctRange.to),
      },
      answers: question.type === QuizQuestionType.TEXT ? [] : (question.answers ?? []).map((answer) => ({
        id: answer._id ?? makeLocalId("answer"),
        originalId: answer._id,
        riskFlag: answer.riskFlag ?? false,
        text: syncLocalizedTextLanguages(answer.text, languages),
        isCorrect: Boolean(answer.isCorrect),
        staticPoints:
          answer.staticPoints === undefined ? "" : String(answer.staticPoints),
      })),
    })),
    interpretations: interpretations.map((interpretation, index) => ({
      id: `interpretation-${index}`,
      title: syncLocalizedTextLanguages(interpretation.title, languages),
      formula: interpretation.formula,
      bands: interpretation.interpretation.map((band, bandIndex) => ({
        id: `interpretation-${index}-band-${bandIndex}`,
        range: {
          from: band.range.from === undefined ? "" : String(band.range.from),
          to: band.range.to === undefined ? "" : String(band.range.to),
        },
        text: syncLocalizedTextLanguages(band.text, languages),
      })),
    })),
  };
};

const trimLocalizedText = (
  value: LocalizedText[],
  languages: string[],
  keepEmpty = false,
) =>
  languages
    .map((language) => ({
      language,
      text: getTextForLanguage(value, language).trim(),
    }))
    .filter((item) => keepEmpty || item.text.length > 0);

const hasTextInLanguage = (value: LocalizedText[], language: string) =>
  getTextForLanguage(value, language).trim().length > 0;

export const hasLanguageContent = (state: QuizFormState, language: string) => {
  if (hasTextInLanguage(state.title, language)) return true;
  if (hasTextInLanguage(state.description, language)) return true;
  if (state.chapters.some((chapter) =>
    hasTextInLanguage(chapter.title ?? [], language) ||
    hasTextInLanguage(chapter.description ?? [], language))) return true;

  if (
    state.interpretations.some(
      (interpretation) =>
        hasTextInLanguage(interpretation.title, language) ||
        interpretation.bands.some((band) =>
          hasTextInLanguage(band.text, language),
        ),
    )
  ) {
    return true;
  }

  return state.questions.some(
    (question) =>
      hasTextInLanguage(question.text, language) ||
      question.answers.some((answer) =>
        hasTextInLanguage(answer.text, language),
      ),
  );
};

export const syncStateLanguages = (
  state: QuizFormState,
  languages: string[],
): QuizFormState => ({
  ...state,
  selectedLanguages: languages,
  title: syncLocalizedTextLanguages(state.title, languages),
  description: syncLocalizedTextLanguages(state.description, languages),
  chapters: state.chapters.map((chapter) => ({
    ...chapter,
    ...(chapter.title !== undefined ? { title: syncLocalizedTextLanguages(chapter.title, languages) } : {}),
    ...(chapter.description !== undefined ? { description: syncLocalizedTextLanguages(chapter.description, languages) } : {}),
  })),
  questions: state.questions.map((question) => ({
    ...question,
    text: syncLocalizedTextLanguages(question.text, languages),
    answers: question.answers.map((answer) => ({
      ...answer,
      text: syncLocalizedTextLanguages(answer.text, languages),
    })),
  })),
  interpretations: state.interpretations.map((interpretation) => ({
    ...interpretation,
    title: syncLocalizedTextLanguages(interpretation.title, languages),
    bands: interpretation.bands.map((band) => ({
      ...band,
      text: syncLocalizedTextLanguages(band.text, languages),
    })),
  })),
});

export const getScoreQuestionMaximum = (question: QuizFormQuestion) => {
  if (question.type === QuizQuestionType.MULTIPLE) {
    return question.answers.reduce((total, answer) => {
      const staticPoints = Number(answer.staticPoints);
      return answer.isCorrect &&
        Number.isFinite(staticPoints) &&
        staticPoints > 0
        ? total + staticPoints
        : total;
    }, 0);
  }

  const points = Number(question.points);
  return Number.isFinite(points) && points > 0 ? points : 0;
};

export const getScoreQuizMaximum = (state: QuizFormState) =>
  state.questions.reduce(
    (total, question) => total + getScoreQuestionMaximum(question),
    0,
  );

export const getRetakeSummary = (retakeAmount: string) => {
  const value = Number(retakeAmount);
  if (value === 0) return "Unlimited retakes";
  if (value === 1) return "One retake";
  if (Number.isInteger(value) && value > 1) return `${value} retakes`;
  return "Enter a non-negative whole number";
};

export const validateQuizForm = (state: QuizFormState): string[] => {
  const errors: string[] = [];
  const languages = state.selectedLanguages;
  const questions = getFormQuestions(state);
  if (!state.chapters.length) errors.push("Add at least one Quiz Chapter.");
  if (questions.length !== state.questions.length) errors.push("Assign every question to an existing Quiz Chapter.");
  state.chapters.forEach((chapter, index) => {
    (["title", "description"] as const).forEach((field) => {
      if (!(chapter[field] ?? []).some((item) => item.text.trim())) return;
      languages.forEach((language) => {
        if (!hasTextInLanguage(chapter[field] ?? [], language)) {
          errors.push(`Quiz Chapter ${index + 1} ${field} is required for ${language}.`);
        }
      });
    });
  });

  if (!languages.length) errors.push("Select at least one language.");
  if (!state.type) errors.push("Select a System Quiz Type.");
  if (!state.evaluationType) errors.push("Select an evaluation type.");

  if (
    state.type === QuizType.FEEDBACK &&
    state.evaluationType === QuizEvaluationType.SCORE
  ) {
    errors.push("Feedback templates do not support score evaluation.");
  }

  const retakeAmount = Number(state.retakeAmount);
  if (!Number.isInteger(retakeAmount) || retakeAmount < 0) {
    errors.push("Allowed retakes must be a non-negative whole number.");
  }

  if (shouldUseQuizScoreSettings(state.evaluationType)) {
    const minimumPoints = Number(state.minimumPoints);
    const maximumPoints = getScoreQuizMaximum(state);
    if (!Number.isFinite(minimumPoints) || minimumPoints < 0) {
      errors.push("Minimum points must be a non-negative number.");
    } else if (minimumPoints > maximumPoints) {
      errors.push("Minimum points cannot exceed the calculated maximum score.");
    }
  }

  languages.forEach((language) => {
    if (!hasTextInLanguage(state.title, language)) {
      errors.push(`Title is required for ${language}.`);
    }
  });

  if (!questions.length) errors.push("Add at least one question.");

  questions.forEach((question, questionIndex) => {
    const questionNumber = questionIndex + 1;

    languages.forEach((language) => {
      if (!hasTextInLanguage(question.text, language)) {
        errors.push(
          `Question ${questionNumber} text is required for ${language}.`,
        );
      }
    });

    if (
      question.type !== QuizQuestionType.RANGE &&
      question.type !== QuizQuestionType.TEXT &&
      question.answers.length < 2
    ) {
      errors.push(`Question ${questionNumber} must have at least two answers.`);
    }

    const correctAnswers = question.answers.filter(
      (answer) => answer.isCorrect,
    );
    if (state.evaluationType === QuizEvaluationType.INTERPRETATION) {
      if (question.type === QuizQuestionType.TEXT) {
        return;
      }

      if (question.type === QuizQuestionType.RANGE) {
        const from = Number(question.range.from);
        const to = Number(question.range.to);
        const hasFiniteBounds =
          question.range.from.trim() !== "" &&
          question.range.to.trim() !== "" &&
          Number.isFinite(from) &&
          Number.isFinite(to);

        if (!hasFiniteBounds) {
          errors.push(
            `Question ${questionNumber} allowed range requires finite From and To values.`,
          );
        } else if (from > to) {
          errors.push(
            `Question ${questionNumber} allowed range From must not exceed To.`,
          );
        }
      } else {
        question.answers.forEach((answer, answerIndex) => {
          const answerNumber = answerIndex + 1;
          const staticPoints = Number(answer.staticPoints);
          if (
            answer.staticPoints.trim() === "" ||
            !Number.isFinite(staticPoints) ||
            !Number.isInteger(staticPoints)
          ) {
            errors.push(
              `Question ${questionNumber}, answer ${answerNumber} static points are required and must be a finite integer.`,
            );
          }
        });
      }
    } else if (state.evaluationType === QuizEvaluationType.RESPONSE_ONLY) {
      if (question.type === QuizQuestionType.RANGE) {
        const from = Number(question.range.from);
        const to = Number(question.range.to);
        const hasFiniteBounds =
          question.range.from.trim() !== "" &&
          question.range.to.trim() !== "" &&
          Number.isFinite(from) &&
          Number.isFinite(to);

        if (!hasFiniteBounds) {
          errors.push(
            `Question ${questionNumber} allowed range requires finite From and To values.`,
          );
        } else if (from > to) {
          errors.push(
            `Question ${questionNumber} allowed range From must not exceed To.`,
          );
        }
      }
    } else if (question.type === QuizQuestionType.TEXT) {
      const points = Number(question.points);
      if (!Number.isFinite(points) || points <= 0) {
        errors.push(`Question ${questionNumber} participation points must be positive and finite.`);
      }
    } else if (question.type === QuizQuestionType.SINGLE) {
      const points = Number(question.points);
      if (!Number.isFinite(points) || points <= 0) {
        errors.push(
          `Question ${questionNumber} points must be greater than 0.`,
        );
      }
      if (correctAnswers.length !== 1) {
        errors.push(
          `Question ${questionNumber} must have exactly one correct answer.`,
        );
      }
    } else if (question.type === QuizQuestionType.MULTIPLE) {
      if (correctAnswers.length < 1) {
        errors.push(
          `Question ${questionNumber} must have at least one correct answer.`,
        );
      }

      question.answers.forEach((answer, answerIndex) => {
        const answerNumber = answerIndex + 1;
        const staticPoints = Number(answer.staticPoints);

        if (
          answer.staticPoints.trim() === "" ||
          !Number.isFinite(staticPoints)
        ) {
          errors.push(
            `Question ${questionNumber}, answer ${answerNumber} static points are required and must be finite.`,
          );
        } else if (answer.isCorrect && staticPoints <= 0) {
          errors.push(
            `Question ${questionNumber}, answer ${answerNumber} is correct and must have positive static points.`,
          );
        } else if (!answer.isCorrect && staticPoints > 0) {
          errors.push(
            `Question ${questionNumber}, answer ${answerNumber} is incorrect and must have zero or negative static points.`,
          );
        }
      });
    } else if (question.type === QuizQuestionType.RANGE) {
      const points = Number(question.points);
      const rangeFrom = Number(question.range.from);
      const rangeTo = Number(question.range.to);
      const correctFrom = Number(question.correctRange.from);
      const correctTo = Number(question.correctRange.to);
      const hasValidRangeBounds =
        question.range.from.trim() !== "" &&
        question.range.to.trim() !== "" &&
        Number.isFinite(rangeFrom) &&
        Number.isFinite(rangeTo);
      const hasValidCorrectBounds =
        question.correctRange.from.trim() !== "" &&
        question.correctRange.to.trim() !== "" &&
        Number.isFinite(correctFrom) &&
        Number.isFinite(correctTo);

      if (!Number.isFinite(points) || points <= 0) {
        errors.push(
          `Question ${questionNumber} points must be greater than 0.`,
        );
      }
      if (!hasValidRangeBounds) {
        errors.push(
          `Question ${questionNumber} allowed range requires finite From and To values.`,
        );
      } else if (rangeFrom > rangeTo) {
        errors.push(
          `Question ${questionNumber} allowed range From must not exceed To.`,
        );
      }
      if (!hasValidCorrectBounds) {
        errors.push(
          `Question ${questionNumber} correct range requires finite From and To values.`,
        );
      } else if (correctFrom > correctTo) {
        errors.push(
          `Question ${questionNumber} correct range From must not exceed To.`,
        );
      } else if (
        hasValidRangeBounds &&
        rangeFrom <= rangeTo &&
        (correctFrom < rangeFrom || correctTo > rangeTo)
      ) {
        errors.push(
          `Question ${questionNumber} correct range must be contained inside the allowed range.`,
        );
      }
    }

    if (
      question.type !== QuizQuestionType.RANGE &&
      question.type !== QuizQuestionType.TEXT
    ) {
      question.answers.forEach((answer, answerIndex) => {
        const answerNumber = answerIndex + 1;
        languages.forEach((language) => {
          if (!hasTextInLanguage(answer.text, language)) {
            errors.push(
              `Question ${questionNumber}, answer ${answerNumber} text is required for ${language}.`,
            );
          }
        });
      });
    }
  });

  if (state.evaluationType === QuizEvaluationType.INTERPRETATION) {
    const referenceableFormulaPositions = questions
      .map((question, index) =>
        question.type === QuizQuestionType.TEXT ? null : index + 1,
      )
      .filter((position): position is number => position !== null);

    if (!state.interpretations.length) {
      errors.push("Add an interpretation dimension.");
    }

    state.interpretations.forEach((interpretation, interpretationIndex) => {
      const dimensionNumber = interpretationIndex + 1;

      languages.forEach((language) => {
        if (!hasTextInLanguage(interpretation.title, language)) {
          errors.push(
            `Interpretation ${dimensionNumber} title is required for ${language}.`,
          );
        }
      });

      const formulaResult = parseInterpretationFormula(
        interpretation.formula,
        questions.length,
        { referenceablePositions: referenceableFormulaPositions },
      );
      if (!formulaResult.valid) {
        errors.push(
          `Interpretation ${dimensionNumber} formula: ${formulaResult.error}`,
        );
      }

      if (!interpretation.bands.length) {
        errors.push(
          `Interpretation ${dimensionNumber} requires a result band.`,
        );
      }

      const bandErrors = getInterpretationBandErrors(interpretation.bands);
      const outputRange = getInterpretationOutputRange(
        interpretation.formula,
        questions,
      );
      if (formulaResult.valid && !outputRange.valid) {
        errors.push(
          `Interpretation ${dimensionNumber} formula output range: ${outputRange.error}`,
        );
      } else if (outputRange.valid) {
        getInterpretationCoverageErrors(
          interpretation.bands,
          outputRange,
        ).forEach((coverageError) => {
          errors.push(`Interpretation ${dimensionNumber}: ${coverageError}`);
        });
      }

      interpretation.bands.forEach((band, bandIndex) => {
        const bandNumber = bandIndex + 1;
        bandErrors[bandIndex].forEach((bandError) => {
          errors.push(
            `Interpretation ${dimensionNumber}, band ${bandNumber}: ${bandError}`,
          );
        });

        languages.forEach((language) => {
          if (!hasTextInLanguage(band.text, language)) {
            errors.push(
              `Interpretation ${dimensionNumber}, band ${bandNumber} text is required for ${language}.`,
            );
          }
        });
      });
    });
  }

  return errors;
};

const buildQuestions = (
  state: QuizFormState,
  includeIds: boolean,
  questions = getFormQuestions(state),
) => {
  const languages = state.selectedLanguages;
  return questions.map((question) => ({
    ...(includeIds && question.originalId ? { _id: question.originalId } : {}),
    text: trimLocalizedText(question.text, languages, true),
    type: question.type,
    ...(state.evaluationType === QuizEvaluationType.SCORE &&
    question.type !== QuizQuestionType.MULTIPLE
      ? { points: Number(question.points) }
      : {}),
    ...(question.type === QuizQuestionType.RANGE
      ? {
          range: {
            from: Number(question.range.from),
            to: Number(question.range.to),
          },
        }
      : {}),
    ...(state.evaluationType === QuizEvaluationType.SCORE &&
    question.type === QuizQuestionType.RANGE
      ? {
          correctRange: {
            from: Number(question.correctRange.from),
            to: Number(question.correctRange.to),
          },
        }
      : {}),
    answers:
      question.type === QuizQuestionType.RANGE
        ? []
        : question.type === QuizQuestionType.TEXT
          ? []
          : question.answers.map((answer) => ({
              ...(includeIds && answer.originalId
                ? { _id: answer.originalId }
                : {}),
              text: trimLocalizedText(answer.text, languages, true),
              riskFlag: answer.riskFlag ?? false,
              ...(state.evaluationType === QuizEvaluationType.SCORE
                ? { isCorrect: answer.isCorrect }
                : state.evaluationType === QuizEvaluationType.INTERPRETATION
                  ? { staticPoints: Number(answer.staticPoints) }
                  : {}),
              ...(state.evaluationType === QuizEvaluationType.SCORE &&
              question.type === QuizQuestionType.MULTIPLE &&
              answer.staticPoints !== ""
                ? { staticPoints: Number(answer.staticPoints) }
                : {}),
            })),
  }));
};

const buildChapters = (state: QuizFormState, includeIds: boolean): QuizChapter[] =>
  state.chapters.map((chapter) => ({
    ...(includeIds && chapter.originalId ? { _id: chapter.originalId } : {}),
    ...(chapter.title !== undefined ? { title: chapter.title.map((item) => ({ ...item })) } : {}),
    ...(chapter.description !== undefined ? { description: chapter.description.map((item) => ({ ...item })) } : {}),
    questions: buildQuestions(state, includeIds, getChapterQuestions(state, chapter.id)),
  }));

export const buildQuizPayload = (state: QuizFormState): NewChapterQuiz => {
  const languages = state.selectedLanguages;
  const description = trimLocalizedText(state.description, languages);
  const isFeedbackTemplate = isFeedbackQuizType(state.type);

  return {
    title: trimLocalizedText(state.title, languages, true),
    ...(description.length ? { description } : {}),
    evaluationType: state.evaluationType as QuizEvaluationType,
    type: state.type as QuizType,
    isSystemTemplate: true,
    ...(isFeedbackTemplate
      ? {}
      : {
          isConfidential: state.isConfidential,
          isEditable: state.isEditable,
          isScoreShown: state.isScoreShown,
        }),
    ...(state.evaluationType === QuizEvaluationType.INTERPRETATION
      ? {
          interpretations: state.interpretations.map((interpretation) => ({
            title: trimLocalizedText(interpretation.title, languages, true),
            formula: normalizeFormulaQuestionReferences(
              interpretation.formula.trim(),
            ),
            interpretation: interpretation.bands.map((band) => ({
              range: {
                ...(band.range.from.trim() !== ""
                  ? { from: Number(band.range.from) }
                  : {}),
                ...(band.range.to.trim() !== ""
                  ? { to: Number(band.range.to) }
                  : {}),
              },
              text: trimLocalizedText(band.text, languages, true),
            })),
          })),
        }
      : {}),
    quiz: {
      chapters: buildChapters(state, false),
      ...(shouldUseQuizScoreSettings(state.evaluationType)
        ? { minimumPoints: Number(state.minimumPoints) }
        : {}),
      retakeAmount: isFeedbackTemplate ? 1 : Number(state.retakeAmount),
    },
  };
};

export const buildQuizUpdatePayload = (
  state: QuizFormState,
  quizId: string,
): Partial<ChapterQuiz> & Pick<ChapterQuiz, "_id"> => {
  const createPayload = buildQuizPayload(state);

  return {
    ...createPayload,
    _id: quizId,
    ...(state.evaluationType === QuizEvaluationType.SCORE
      ? { interpretations: [] }
      : {}),
    quiz: {
      ...createPayload.quiz,
      chapters: buildChapters(state, true),
    },
  };
};

export const getQuizDisplayTitle = (quiz: Pick<Quiz, "title">) => {
  const firstTitle = quiz.title.find((item) => item.text.trim().length > 0);
  if (!firstTitle) return "Untitled System Quiz";

  return `${firstTitle.text}${firstTitle.language ? ` (${firstTitle.language})` : ""}`;
};
